import { useEffect, useRef, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';
import { Button, Skeleton } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { useMonthBudget, useMonthBudgetMutations, type MonthBudget } from './budgetApi';

/**
 * מכסת שעות לחודש אחד, במקום שבו רואים את המספר.
 *
 * נפתח מהמד של החודש בכרטיס העובד, כי זה הרגע שבו מבינים
 * שהמכסה הקבועה אינה מתאימה לחודש הזה. מילואים, תקופת מבחנים
 * או עונה חזקה אינם שינוי קבוע, ומי שמשנה את המספר על העובד
 * שוכח להחזיר אותו.
 *
 * ריק = חזרה למכסה הקבועה לאותו שדה, ולא "בלי מכסה". לכן יש
 * כפתור נפרד שמוחק את הדריסה כולה.
 */
export function MonthBudgetDialog({
  employeeId,
  employeeName,
  month,
  monthLabel,
  defaults,
  onClose,
}: {
  employeeId: string;
  employeeName: string;
  /** הראשון בחודש, yyyy-MM-dd */
  month: string;
  monthLabel: string;
  /** המכסה הקבועה של העובד, להצגה כברירת מחדל */
  defaults: { weekly: number | null; monthly: number | null };
  onClose: () => void;
}) {
  const budget = useMonthBudget(employeeId, month);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`מכסת שעות ל${monthLabel}`}
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            מכסה ל{monthLabel} · {employeeName}
          </h2>
          <button
            ref={closeRef}
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <p className="m-0 text-body-sm text-muted">
          חל על {monthLabel} בלבד. בחודש הבא המכסה חוזרת לקבועה מעצמה.
        </p>

        {budget.isPending ? (
          <Skeleton className="h-40" />
        ) : (
          // הטופס נולד אחרי שהנתון הגיע, ולכן הוא יכול לאתחל את
          // עצמו פעם אחת. אתחול מתוך effect היה דורס מה שהוקלד
          // בכל רענון של השאילתה.
          <BudgetForm
            employeeId={employeeId}
            month={month}
            monthLabel={monthLabel}
            defaults={defaults}
            initial={budget.data ?? null}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function BudgetForm({
  employeeId,
  month,
  monthLabel,
  defaults,
  initial,
  onClose,
}: {
  employeeId: string;
  month: string;
  monthLabel: string;
  defaults: { weekly: number | null; monthly: number | null };
  initial: MonthBudget | null;
  onClose: () => void;
}) {
  const { save, clear } = useMonthBudgetMutations();
  const [weekly, setWeekly] = useState(initial?.weekly_hours?.toString() ?? '');
  const [monthly, setMonthly] = useState(initial?.monthly_hours?.toString() ?? '');
  const [note, setNote] = useState(initial?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  function run(promise: Promise<unknown>) {
    setError(null);
    void promise.then(onClose).catch((caught: unknown) => setError(errorMessage(caught)));
  }

  return (
    <>
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <form
        className="grid gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          run(
            save.mutateAsync({
              employee_id: employeeId,
              month,
              weekly_hours: weekly ? Number(weekly) : null,
              monthly_hours: monthly ? Number(monthly) : null,
              note: note.trim() || null,
            }),
          );
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <HoursInput
            id="budget-weekly"
            label="שעות בשבוע"
            value={weekly}
            fallback={defaults.weekly}
            max={168}
            onChange={setWeekly}
          />
          <HoursInput
            id="budget-monthly"
            label="שעות בחודש"
            value={monthly}
            fallback={defaults.monthly}
            max={744}
            onChange={setMonthly}
          />
        </div>

        <label className="grid gap-1">
          <span className="text-label font-medium">הסיבה</span>
          <input
            type="text"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="למשל: מילואים"
            className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink placeholder:text-muted"
          />
        </label>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            שמירת המכסה ל{monthLabel}
          </Button>
          {initial !== null && (
            <Button
              variant="secondary"
              disabled={clear.isPending}
              onClick={() => run(clear.mutateAsync({ employee_id: employeeId, month }))}
            >
              <RotateCcw size={18} strokeWidth={1.75} aria-hidden="true" />
              חזרה למכסה הקבועה
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </form>
    </>
  );
}

/** שדה שעות שמראה מה יקרה אם ישאירו אותו ריק. */
function HoursInput({
  id,
  label,
  value,
  fallback,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  fallback: number | null;
  max: number;
  onChange: (next: string) => void;
}) {
  return (
    <div className="grid content-start gap-1">
      <label htmlFor={id} className="text-label font-medium">
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        dir="ltr"
        min={1}
        max={max}
        step={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={`${id}-hint`}
        className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
      />
      <p id={`${id}-hint`} className="m-0 text-body-sm text-muted">
        {value === ''
          ? fallback === null
            ? 'ריק: בלי מכסה'
            : `ריק: המכסה הקבועה, ${fallback} שעות`
          : 'חל על החודש הזה בלבד'}
      </p>
    </div>
  );
}
